/**
 * Realce de markdown para o compositor.
 *
 * Não é um parser: é uma varredura por linha que devolve o texto **inteiro**,
 * fatiado em trechos com um nome de cor. Inteiro é a palavra importante — o
 * resultado é desenhado atrás de um `textarea` transparente, e um caractere a
 * mais ou a menos desalinha o cursor de quem está escrevendo.
 *
 * Os marcadores continuam à vista (`# `, `**`, a crase), como o usuário pediu:
 * quem escreve markdown quer ver o markdown, só que legível.
 */

export type Cor =
  | 'normal'
  | 'titulo'
  | 'forte'
  | 'enfase'
  | 'codigo'
  | 'cerca'
  | 'citacao'
  | 'marca'
  | 'link';

export interface Trecho {
  texto: string;
  cor: Cor;
}

/** Um trecho em linha por vez, na ordem em que a cerca do markdown os define. */
const EM_LINHA = new RegExp(
  [
    '(`[^`\\n]+`)', // código
    '(\\*\\*[^*\\n]+\\*\\*|__[^_\\n]+__)', // negrito
    '(\\*[^*\\n]+\\*|_[^_\\n]+_)', // itálico
    '(\\[[^\\]\\n]*\\]\\([^)\\n]*\\))', // link
  ].join('|'),
  'g',
);

const COR_DO_GRUPO: Cor[] = ['codigo', 'forte', 'enfase', 'link'];

function dentroDaLinha(linha: string): Trecho[] {
  const trechos: Trecho[] = [];
  let ultimo = 0;
  EM_LINHA.lastIndex = 0;

  for (let achado = EM_LINHA.exec(linha); achado; achado = EM_LINHA.exec(linha)) {
    if (achado.index > ultimo) {
      trechos.push({ texto: linha.slice(ultimo, achado.index), cor: 'normal' });
    }
    const grupo = COR_DO_GRUPO.findIndex((_, i) => achado[i + 1] !== undefined);
    trechos.push({ texto: achado[0], cor: COR_DO_GRUPO[grupo] ?? 'normal' });
    ultimo = achado.index + achado[0].length;
  }

  if (ultimo < linha.length) trechos.push({ texto: linha.slice(ultimo), cor: 'normal' });
  return trechos;
}

export function realcarMarkdown(texto: string): Trecho[] {
  const saida: Trecho[] = [];
  let emCerca = false;

  const linhas = texto.split('\n');
  linhas.forEach((linha, indice) => {
    if (indice > 0) saida.push({ texto: '\n', cor: 'normal' });

    // A cerca liga e desliga, e a própria linha da cerca já entra colorida.
    if (/^\s*```/.test(linha)) {
      emCerca = !emCerca;
      saida.push({ texto: linha, cor: 'cerca' });
      return;
    }
    if (emCerca) {
      saida.push({ texto: linha, cor: 'cerca' });
      return;
    }
    if (linha.length === 0) return;

    if (/^#{1,6}\s/.test(linha)) {
      saida.push({ texto: linha, cor: 'titulo' });
      return;
    }
    if (/^\s*>/.test(linha)) {
      saida.push({ texto: linha, cor: 'citacao' });
      return;
    }

    // Marcador de lista: só o marcador ganha cor; o resto da linha é texto
    // normal, e continua passando pelo realce em linha.
    const lista = /^(\s*(?:[-*+]|\d+\.)\s)/.exec(linha);
    if (lista) {
      saida.push({ texto: lista[1] as string, cor: 'marca' });
      saida.push(...dentroDaLinha(linha.slice((lista[1] as string).length)));
      return;
    }

    saida.push(...dentroDaLinha(linha));
  });

  return saida;
}
